import React from 'react';

import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  TextField,
  Typography,
  Collapse,
} from '@packages/shared';
import { useIntl } from 'react-intl';
import { EngravingMoreInformation } from './EngravingMoreInformation';
import { ShowMoreInformationLink } from './ShowMoreInformationLink';
import { engravingInputMessages } from './messages';
import { InformationBox } from '../InformationBox';
import { validator } from './validator';

export type AddToBasketHandler = () => null | string;

export interface EngravingInputProps {
  /**
   * is the input mandatory
   */
  mandatory: boolean;
  /**
   * the max length of the input
   */
  maxLength: number;
}

export interface EngravingInputHandle {
  /**
   * addToBasket handler function
   *
   * will be executed when addToBasket is executed and validate the data and handles errors
   *
   * @returns null | string - null if validation fails - string representing a validated input which can be used into the basket
   */
  addToBasketHandler: AddToBasketHandler;
}

interface InputValue {
  value: string;
  error: string | null;
}

/**
 * EngravingInput component description displayed in storybook
 * */
const EngravingInputComponent: React.ForwardRefRenderFunction<
  EngravingInputHandle,
  EngravingInputProps
> = ({ mandatory, maxLength }, forwardedRef) => {
  const format = useIntl().formatMessage;

  const [inputValue, setInputValue] = React.useState<InputValue>({ value: '', error: null });
  const [showMoreInformation, setShowMoreInformation] = React.useState<boolean>(false);

  React.useImperativeHandle(forwardedRef, () => ({
    addToBasketHandler() {
      const { value: validatedValue, error } = validator(inputValue.value, maxLength, mandatory);

      if (error !== null) {
        setInputValue({
          value: validatedValue,
          error: format(
            error === 'MAXLENGTH'
              ? engravingInputMessages.errorgMaxLength
              : engravingInputMessages.errorMandatory,
          ),
        });
        return null;
      }
      setInputValue({
        value: validatedValue,
        error: null,
      });

      return validatedValue;
    },
  }));

  const listItems = [
    format(engravingInputMessages.informationP1),
    format(engravingInputMessages.informationP2),
    format(engravingInputMessages.informationP3),
    format(engravingInputMessages.informationP4),
    format(engravingInputMessages.informationP5),
  ];

  function onChangeHandler(value: string): void {
    const { value: validatedValue, error } = validator(value, maxLength, mandatory);

    if (error === 'MAXLENGTH') {
      setInputValue({
        value: validatedValue,
        error: format(engravingInputMessages.errorgMaxLength),
      });
    } else {
      setInputValue({
        value: validatedValue,
        error: null,
      });
    }
  }
  function moreInformationHandler() {
    setShowMoreInformation((prev) => !prev);
  }

  return (
    <Card>
      <CardHeader title={format(engravingInputMessages.cardHeadline)} />
      <Divider />
      <CardContent>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2">{format(engravingInputMessages.label)}</Typography>
            <Typography
              variant="body2"
              sx={{ color: inputValue.error === null ? 'text.darkTransparent' : 'error.main' }}
            >
              {format(engravingInputMessages.maxLengthIndicator, {
                length: inputValue.value.length,
                maxlength: maxLength,
              })}
            </Typography>
          </Box>
          <TextField
            value={inputValue.value.toUpperCase()}
            placeholder={format(engravingInputMessages.placeholder)}
            fullWidth
            sx={{ p: 0 }}
            onChange={(event) => onChangeHandler(event.target.value)}
            error={inputValue.error !== null}
            helperText={inputValue.error}
          />
        </Box>
        <InformationBox items={listItems} type="LIST" />
      </CardContent>
      <Collapse in={showMoreInformation}>
        <EngravingMoreInformation />
      </Collapse>
      <Divider />
      <ShowMoreInformationLink
        showMoreInformation={showMoreInformation}
        moreInformationHandler={() => moreInformationHandler()}
      />
    </Card>
  );
};

export const EngravingInput = React.forwardRef(EngravingInputComponent);
